import { logger } from "@src/utils/logger"
import type { QueryObserverOptions } from "@tanstack/react-query"
import type { IntentsUserId } from "../../types/intentsUserId"
import {
  type CurrentEpochEstimate,
  type PoolSnapshot,
  type UserWithdrawalRequest,
  nearStakingContract,
} from "./contract"

export const nearStakingQueryKeys = {
  all: ["near_staking"] as const,
  poolSnapshots: () => [...nearStakingQueryKeys.all, "pool_snapshot"] as const,
  poolSnapshot: (contractId: string | null) =>
    [...nearStakingQueryKeys.poolSnapshots(), { contractId }] as const,
  currentEpoch: () => [...nearStakingQueryKeys.all, "current_epoch"] as const,
  withdrawalRequests: () =>
    [...nearStakingQueryKeys.all, "withdrawal_request"] as const,
  userWithdrawalRequest: (
    contractId: string | null,
    userId: IntentsUserId | null
  ) =>
    [
      ...nearStakingQueryKeys.withdrawalRequests(),
      { contractId, userId },
    ] as const,
}

export function createPoolSnapshotQueryOptions(
  contractId: string | null
): QueryObserverOptions<PoolSnapshot | null> {
  return {
    queryKey: nearStakingQueryKeys.poolSnapshot(contractId),
    queryFn: async () => {
      if (contractId == null) return null
      try {
        return await nearStakingContract.getPoolSnapshot(contractId)
      } catch (err) {
        logger.error(
          err instanceof Error
            ? err
            : new Error("Failed to load LST pool snapshot", { cause: err })
        )
        return null
      }
    },
    enabled: contractId != null,
    staleTime: 30_000,
    refetchInterval: 30_000,
  }
}

export function createCurrentEpochQueryOptions(
  enabled: boolean
): QueryObserverOptions<CurrentEpochEstimate | null> {
  return {
    queryKey: nearStakingQueryKeys.currentEpoch(),
    queryFn: async () => {
      try {
        return await nearStakingContract.getCurrentEpochEstimate()
      } catch (err) {
        logger.error(
          err instanceof Error
            ? err
            : new Error("Failed to load NEAR current epoch", { cause: err })
        )
        return null
      }
    },
    enabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
  }
}

export function createUserWithdrawalRequestQueryOptions(
  contractId: string | null,
  userId: IntentsUserId | null
): QueryObserverOptions<UserWithdrawalRequest | null> {
  return {
    queryKey: nearStakingQueryKeys.userWithdrawalRequest(contractId, userId),
    queryFn: async () => {
      if (contractId == null || userId == null) return null
      try {
        return await nearStakingContract.getUserWithdrawalRequest(
          contractId,
          userId
        )
      } catch (err) {
        logger.error(
          err instanceof Error
            ? err
            : new Error("Failed to load LST withdrawal request", { cause: err })
        )
        return null
      }
    },
    enabled: contractId != null && userId != null,
    staleTime: 30_000,
    refetchInterval: 30_000,
  }
}
